import useTranslator from "../../hooks/useTranslator";
import { useMemo } from "react";
import { canonicalizeMissingDocKey } from "../../Utils/missingDocNote";
import styles from "./MissingDocumentsExplanationBlock.module.css";

// Presentational block for the tenant missing-documents explanation note.
// Used in the sign-up documents step and in the profile modal — the same
// block in both places, writing the same note (TenantMissingDocNote).
// Selection is canonical-key based (one entry per document slot) so the sign-up
// and profile vocabularies — which name documents slightly differently —
// never duplicate each other and stay interchangeable.
// Props:
// - missingDocuments: keys of the documents Skor did not detect.
// - selectedExplainedKeys: keys the tenant selected to explain.
// - onToggleExplainedKey: toggles one document in the selection.
// - explanationMessage / onExplanationMessageChange: the single comment that
//   covers all selected documents. Shared with landlords, never marks a
//   document as received, never changes the score.
// - translateDocName: maps a document key to its display label.
// - idPrefix: unique prefix for the comment box id (signup vs modal).
const MissingDocumentsExplanationBlock = ({
  missingDocuments = [],
  selectedExplainedKeys = [],
  onToggleExplainedKey,
  explanationMessage = "",
  onExplanationMessageChange,
  translateDocName,
  idPrefix = "missing-documents-explanation",
  disabled = false,
}) => {
  const { _t } = useTranslator();
  const showExplanationBox = selectedExplainedKeys.length > 0;
  // Canonical-key set of the current selection: checkbox state and toggling
  // match by canonical key, so equivalent keys from either surface behave as
  // one entry while distinct payslip slots stay independent.
  const selectedCanonicalKeys = useMemo(
    () =>
      new Set(
        (selectedExplainedKeys ?? []).map((selectedKey) =>
          canonicalizeMissingDocKey(selectedKey)
        )
      ),
    [selectedExplainedKeys]
  );
  const displayName = (docKey) =>
    typeof translateDocName === "function" ? translateDocName(docKey) : docKey;
  const commentBoxId = `${idPrefix}-comment`;

  return (
    <div className={styles.explainedBlock}>
      <p className={styles.explainedDescription}>
        {_t(
          "missingDocExplainIntro",
          "We couldn't match these documents to your uploads. Already uploaded them or can't provide them? Select them and let us know."
        )}
      </p>
      <ul className={styles.explainedList}>
        {missingDocuments.map((docKey) => {
          const isSelected = selectedCanonicalKeys.has(
            canonicalizeMissingDocKey(docKey)
          );
          return (
            <li
              key={docKey}
              className={isSelected ? styles.explainedSelected : undefined}
            >
              <label className={styles.explainedLabel}>
                <input
                  type="checkbox"
                  className={styles.explainedCheckbox}
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onToggleExplainedKey?.(docKey)}
                />
                <img src="/images/docIconGreen.svg" alt="" />
                <span>{displayName(docKey)}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {showExplanationBox && (
        <div className={styles.explanationBox}>
          <label htmlFor={commentBoxId} className={styles.explanationTitle}>
            {_t("missingDocExplainTitle", "Tell the landlord what happened")} ·{" "}
            {selectedExplainedKeys.map(displayName).join(", ")}
          </label>
          <textarea
            id={commentBoxId}
            className={styles.explanationInput}
            value={explanationMessage}
            maxLength={1000}
            disabled={disabled}
            onChange={(event) =>
              onExplanationMessageChange?.(event.target.value)
            }
            placeholder={_t(
              "missingDocExplainPlaceholder",
              "Explain what happened with these documents"
            )}
          />
          <p className={styles.explanationHint}>
            {_t(
              "missingDocExplainHint",
              "Your explanation will be shared with the landlord. We won't remind you about these documents again."
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default MissingDocumentsExplanationBlock;
